import User from 'account/dto/User'
import ConfirmModal from 'components/modal/ConfirmModal'
import { omit } from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import React, { useMemo, useState } from 'react'
import { connect, shallowEqual, useSelector } from 'react-redux'
import MultiContributorsAutocomplete from 'referencial/components/contributor/components/MultiContributorsAutocomplete'
import i18n from 'simple-react-i18n'
import { authorizeContributors, authorizeFilter } from 'utils/HabilitationUtil'
import { removeNullKeys } from 'utils/StoreUtils'
import Input from '../../../../components/forms/Input'
import NumberField from '../../../../components/forms/NumberField'
import Select from '../../../../components/forms/Select'
import Textarea from '../../../../components/forms/Textarea'
import ContributorDto from '../../../../referencial/components/contributor/dto/ContributorDto'
import NetworkDto from '../../../../referencial/components/network/dto/NetworkDto'
import { getMatTypes } from '../../../../utils/MaterielUtils'
import PowerSupplyDto from '../dto/PowerSupplyDto'
import PowerSupplyTypeDto from '../dto/PowerSupplyTypeDto'
import { POWER_SUPPLY_ID } from 'materiel/constants/MaterielConstants'
import { isFieldRequired } from 'materiel/utils/MaterielUtils'
import { DATEACHAT_OBLIGATOIRE, NUMSERIE_OBLIGATOIRE } from 'materiel/constants/MaterielSettingRuleConstant'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'

const PowerSupplyPanel = ({
    active = false,
    disabled = true,
    powerSupply = {},
    contributors = [],
    accountUser = {},
    networks = [],
    powerSupplyTypes = [],
    onChange = () => { },
}) => {
    const {
        materielSettingRules,
    } = useSelector(store => ({
        materielSettingRules: store.MaterielReducer.materielSettingRules,
    }), shallowEqual)

    const [isOpen, setOpen] = useState(false)
    const [selectedType, setSelectedType] = useState()

    const rules = useMemo(() => {
        return materielSettingRules.filter(({ idMaterielType }) => idMaterielType === POWER_SUPPLY_ID)
    }, [materielSettingRules])

    return (
        <div className='col s12 margin-bottom-1'>
            <div className='row no-margin' style={{ paddingTop: '3px' }}>
                <Select
                    options={getMatTypes(powerSupplyTypes, powerSupply.powerSupplyType)}
                    label={i18n.type}
                    col={4}
                    onChange={v => {
                        const type = powerSupplyTypes.find(({ id }) => v === id)
                        if (type) {
                            setOpen(true)
                            setSelectedType(type)
                        } else {
                            onChange({ powerSupplyType: v })
                        }
                    }}
                    value={powerSupply.powerSupplyType}
                    nullLabel='&nbsp;'
                    acive={active}
                    disabled={disabled}
                    obligatory
                />
                <Input
                    col={4}
                    title={i18n.serialNumber}
                    value={powerSupply.serialNumber}
                    onChange={value => onChange({ serialNumber: value })}
                    acive={active}
                    disabled={powerSupply.isEquipment || disabled}
                    obligatory={isFieldRequired(rules, NUMSERIE_OBLIGATOIRE)}
                />
                <Input
                    col={4}
                    title={i18n.immoNumber}
                    value={powerSupply.reference}
                    onChange={value => onChange({ reference: value })}
                    acive={active}
                    disabled={powerSupply.isEquipment || disabled}
                />
            </div>
            <div className='row no-margin' style={{ paddingTop: '3px' }}>
                <MultiContributorsAutocomplete
                    options={authorizeContributors(contributors, accountUser, powerSupply.manufacturerId)}
                    label={i18n.manufacturer}
                    col={6}
                    onChange={(id) => onChange({ manufacturerId: id })}
                    values={powerSupply.manufacturerId}
                    disabled={powerSupply.isEquipment || disabled}
                    keyLabel='labelDisplay'
                />
                <MultiContributorsAutocomplete
                    options={authorizeContributors(contributors, accountUser, powerSupply.providerId)}
                    label={i18n.provider}
                    col={6}
                    onChange={(id) => onChange({ providerId: id })}
                    values={powerSupply.providerId}
                    disabled={powerSupply.isEquipment || disabled}
                    keyLabel='labelDisplay'
                />
            </div>
            <div className='row no-margin' style={{ paddingTop: '3px' }}>
                <Select
                    options={authorizeFilter(networks, accountUser, powerSupply.networkCode)}
                    label={i18n.network}
                    col={12}
                    onChange={value => onChange({ networkCode: value })}
                    value={powerSupply.networkCode}
                    nullLabel='&nbsp;'
                    acive={active}
                    disabled={powerSupply.isEquipment || disabled}
                />
            </div>
            <div className='row no-margin' style={{ paddingTop: '3px' }}>
                <NumberField
                    col={3}
                    title={`${i18n.voltage} (V)`}
                    value={powerSupply.voltage}
                    onChange={v => onChange({ voltage: v })}
                    floatValue
                    acive={active}
                    disabled={disabled}
                />
                <NumberField
                    col={3}
                    title={`${i18n.lifeTime} (${i18n.month})`}
                    value={powerSupply.lifeTime}
                    onChange={v => onChange({ lifeTime: v })}
                    floatValue
                    acive={active}
                    disabled={disabled}
                />
            </div>
            <div className='row no-margin' style={{ paddingTop: '3px' }}>
                <SimpleDatePicker
                    id='manufactureDate'
                    label={i18n.manufactureDate}
                    value={powerSupply.manufactureDate}
                    onChange={v => onChange({ manufactureDate: v })}
                    col={3}
                    disabled={powerSupply.isEquipment || disabled}
                    max={powerSupply.purchaseDate}
                />
                <SimpleDatePicker
                    id='purchaseDate'
                    label={i18n.purchaseDate}
                    value={powerSupply.purchaseDate}
                    onChange={v => onChange({ purchaseDate: v })}
                    col={3}
                    disabled={powerSupply.isEquipment || disabled}
                    min={powerSupply.manufactureDate}
                    obligatory={isFieldRequired(rules, DATEACHAT_OBLIGATOIRE)}
                />
                <SimpleDatePicker
                    id='withdrawalDate'
                    label={i18n.withdrawalDate}
                    value={powerSupply.withdrawalDate}
                    onChange={v => onChange({ withdrawalDate: v })}
                    col={3}
                    disabled={powerSupply.isEquipment || disabled}
                    min={powerSupply.purchaseDate}
                />
                <SimpleDatePicker
                    label={i18n.warrantyExpirationDate}
                    value={powerSupply.warrantyTime && moment(powerSupply.purchaseDate).add(powerSupply.warrantyTime, 'months').valueOf()}
                    col={3}
                    disabled
                />
            </div>
            <div className='row no-margin' style={{ paddingTop: '8px' }}>
                <Textarea
                    col={12}
                    title={i18n.comment}
                    value={powerSupply.comment}
                    onChange={value => onChange({ comment: value })}
                    acive={active}
                    disabled={disabled}
                />
            </div>
            <ConfirmModal
                isOpen={isOpen}
                title={i18n.materielDefaultValuesMessage}
                onValidate={() => {
                    setOpen(false)
                    onChange(removeNullKeys({
                        powerSupplyType: selectedType.id,
                        ...omit(selectedType, ['id', 'code', 'label', 'name', 'comment', 'updateDate', 'updateLogin', 'startDate', 'endDate', 'icon']),
                    }))
                }}
                onReject={() => {
                    setOpen(false)
                    onChange({ powerSupplyType: selectedType.id })
                }}
                onClose={() => setOpen(false)}
            />
        </div>
    )
}


PowerSupplyPanel.propTypes = {
    disabled: PropTypes.bool,
    active: PropTypes.bool,
    onChange: PropTypes.func,
    powerSupply: PropTypes.instanceOf(PowerSupplyDto),
    powerSupplyTypes: PropTypes.arrayOf(PropTypes.instanceOf(PowerSupplyTypeDto)),
    contributors: PropTypes.arrayOf(PropTypes.instanceOf(ContributorDto)),
    networks: PropTypes.arrayOf(PropTypes.instanceOf(NetworkDto)),
    accountUser: PropTypes.instanceOf(User),
}

const mapStateToProps = store => ({
    contributors: store.ContributorReducer.contributors,
    powerSupplyTypes: store.PowerSupplyReducer.powerSupplyTypes,
    networks: store.NetworkReducer.networks,
    accountUser: store.AccountReducer.accountUser,
})

export default connect(mapStateToProps)(PowerSupplyPanel)
